import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { formatEuro, toNumber } from './evaluationFormatters';

export default function IncomeTaxSection({ data }) {
    const revenueRows = [
        {
            label: 'Rechnungen',
            net: toNumber(data?.invoiceNet),
            vat: toNumber(data?.invoiceVat),
            gross: toNumber(data?.invoiceNet) + toNumber(data?.invoiceVat),
        },
        {
            label: 'Sonstige Einnahmen',
            net: toNumber(data?.otherIncomeNet),
            vat: toNumber(data?.otherIncomeVat),
            gross: toNumber(data?.otherIncomeNet) + toNumber(data?.otherIncomeVat),
        },
    ];
    const revenueTotals = {
        net: revenueRows.reduce((a, r) => a + r.net, 0),
        vat: revenueRows.reduce((a, r) => a + r.vat, 0),
        gross: revenueRows.reduce((a, r) => a + r.gross, 0),
    };

    const expenseGrossByCategory = data?.expenseGrossByCategory || {};
    const expenseRows = Object.entries(data?.expenseNetByCategory || {}).map(([cat, net]) => {
        const parsedNet = toNumber(net);
        const parsedGross = expenseGrossByCategory[cat] != null ? toNumber(expenseGrossByCategory[cat]) : parsedNet;
        return {
            category: cat,
            net: parsedNet,
            gross: parsedGross,
        };
    });
    const expenseTotalNet = expenseRows.reduce((a, r) => a + r.net, 0);
    const expenseVat = toNumber(data?.expenseVat);
    let expenseTotalGross = expenseRows.reduce((a, r) => a + r.gross, 0);
    if (expenseTotalGross === expenseTotalNet && expenseVat > 0) {
        expenseTotalGross = expenseTotalNet + expenseVat;
    }
    const contributionTotal = toNumber(data?.nonTaxableContributionTotal);
    const withdrawalTotal = toNumber(data?.nonTaxableWithdrawalTotal);

    const taxDue = toNumber(data?.taxDue);
    const prepayments = toNumber(data?.prepayments);
    const payable = taxDue - prepayments;

    return (
        <>
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    mb: 2,
                    borderColor: payable >= 0 ? 'divider' : 'success.light',
                    background: payable >= 0 ? 'transparent' : 'rgba(46, 160, 67, 0.06)',
                }}
            >
                <CardContent sx={{ py: 2.5 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        <Box sx={{ minWidth: 220, flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Einnahmen gesamt
                            </Typography>
                            <Typography variant="h6">{formatEuro(revenueTotals.net)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Brutto: {formatEuro(revenueTotals.gross)}
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: 220, flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Ausgaben gesamt
                            </Typography>
                            <Typography variant="h6">{formatEuro(expenseTotalNet)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Brutto: {formatEuro(expenseTotalGross)}
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: 220, flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Zu versteuern (netto)
                            </Typography>
                            <Typography variant="h6">{formatEuro(revenueTotals.net - expenseTotalNet)}</Typography>
                        </Box>
                        <Box sx={{ minWidth: 260, flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Nachzahlung / Erstattung
                            </Typography>
                            <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                                <Typography variant="h6">{formatEuro(payable)}</Typography>
                                <Chip
                                    label={payable >= 0 ? 'Nachzahlung' : 'Erstattung'}
                                    color={payable >= 0 ? 'warning' : 'success'}
                                    size="small"
                                    variant="outlined"
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            <Typography variant="h6" sx={{ mb: 1 }}>
                Einnahmen
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, mb: 3 }}>
                <Table aria-label="Tabelle Einnahmen">
                    <TableHead>
                        <TableRow>
                            <TableCell>Quelle</TableCell>
                            <TableCell align="right">Netto</TableCell>
                            <TableCell align="right">USt</TableCell>
                            <TableCell align="right">Brutto</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {revenueRows.map((row) => (
                            <TableRow key={row.label}>
                                <TableCell>{row.label}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 128 }}>
                                    {formatEuro(row.net)}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 128 }}>
                                    {formatEuro(row.vat)}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 128 }}>
                                    {formatEuro(row.gross)}
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Summe</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatEuro(revenueTotals.net)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatEuro(revenueTotals.vat)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatEuro(revenueTotals.gross)}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="h6" sx={{ mb: 1 }}>
                Ausgaben
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3 }}>
                <Table aria-label="Tabelle Ausgaben">
                    <TableHead>
                        <TableRow>
                            <TableCell>Kategorie</TableCell>
                            <TableCell align="right">Netto</TableCell>
                            <TableCell align="right">Brutto</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {expenseRows.map((row) => (
                            <TableRow key={row.category}>
                                <TableCell>{row.category}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 128 }}>
                                    {formatEuro(row.net)}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 128 }}>
                                    {formatEuro(row.gross)}
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Summe</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatEuro(expenseTotalNet)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatEuro(expenseTotalGross)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>abgezogene USt</TableCell>
                            <TableCell align="right">{formatEuro(expenseVat)}</TableCell>
                            <TableCell align="right">-</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Nicht steuerwirksame Bewegungen
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Summe Einlagen
                        </Typography>
                        <Typography variant="h6">{formatEuro(contributionTotal)}</Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Summe Entnahmen
                        </Typography>
                        <Typography variant="h6">{formatEuro(withdrawalTotal)}</Typography>
                    </Paper>
                </Stack>
            </Box>

            {(taxDue || prepayments) ? (
                <Card variant="outlined" sx={{ borderRadius: 3, mt: 2 }}>
                    <CardContent sx={{ py: 1.5 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Box sx={{ minWidth: 220 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Berechnete Einkommensteuer
                                </Typography>
                                <Typography variant="subtitle1">{formatEuro(taxDue)}</Typography>
                            </Box>
                            <Box sx={{ minWidth: 220 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Vorauszahlungen
                                </Typography>
                                <Typography variant="subtitle1">{formatEuro(prepayments)}</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}
        </>
    );
}
